import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateVehicleInput, UpdateVehicleInput, Vehicle } from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useCustomerVehicles(workspaceId: string | null, customerId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'customers', customerId, 'vehicles'],
    queryFn: () => api.get<Vehicle[]>(workspacePath(workspaceId!, `/customers/${customerId}/vehicles`)),
    enabled: !!workspaceId && !!customerId,
  });
}

export function useVehicle(workspaceId: string | null, vehicleId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'vehicles', vehicleId],
    queryFn: () => api.get<Vehicle>(workspacePath(workspaceId!, `/vehicles/${vehicleId}`)),
    enabled: !!workspaceId && !!vehicleId,
  });
}

export function useCreateVehicle(workspaceId: string | null, customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVehicleInput) => api.post<{ id: string }>(workspacePath(workspaceId!, `/customers/${customerId}/vehicles`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'customers', customerId] });
    },
  });
}

export function useUpdateVehicle(workspaceId: string | null, customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, input }: { vehicleId: string; input: UpdateVehicleInput }) =>
      api.patch<void>(workspacePath(workspaceId!, `/vehicles/${vehicleId}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'customers', customerId] });
    },
  });
}

export function useRevealVin(workspaceId: string | null) {
  return useMutation({
    mutationFn: (vehicleId: string) => api.get<{ vin: string }>(workspacePath(workspaceId!, `/vehicles/${vehicleId}/vin`)),
  });
}
